/**
 * CLMS-113 invitation proof fragment lifecycle (T036, hardened by T055/US4).
 *
 * Invitation mail opens `/register/invitation#proof=<opaque-proof>`. On first
 * client execution the fragment is read once, removed immediately with
 * `history.replaceState`, and retained only in memory. The proof never
 * reaches a path, query string, cookie, storage, analytics, logs, or any
 * third-party origin (UI contract, research.md Decision 2).
 */

export interface InvitationProofCapture {
  /** Strict `iv1` proof, or null when the fragment is missing or malformed. */
  proof: string | null;
  /**
   * Per-capture claim capability. Minted with the staged proof and handed
   * out only on the proof-carrying capture: presenting it back to
   * `captureInvitationProofFromLocation` re-reads the staged proof (same
   * logical mount's StrictMode re-render), and it is required by
   * `claimCapturedInvitationProof`. Null on proof-less captures.
   */
  ticket: InvitationProofClaimTicket | null;
}

/**
 * Opaque, unforgeable per-capture claim capability. Only the mount that
 * performed the capture (and therefore holds this ticket in a fiber-local
 * ref) can re-read or claim/park the staged proof — a later unrelated mount
 * can never consume a capture staged by an abandoned render.
 */
export type InvitationProofClaimTicket = symbol;

/**
 * Strict `iv1.<selector>.<secret>` shape: UUID selector (accepted in any
 * case, canonicalized to lowercase like the backend) plus a 43-character
 * unpadded base64url secret. The selector enforces backend-parity UUID
 * structure — version nibble 1-5 and RFC 4122 variant nibble 8/9/a/b — so
 * nil/version-0/wrong-variant selectors never pass pre-validation or invoke
 * lookup (T055). The secret is then round-trip decoded to exactly 32 bytes
 * and re-encoded so only the canonical encoding parses — matching backend
 * canonicality (T052). Anything else — including legacy GUID/composite/
 * reversible identifiers — never parses as a proof and never invokes lookup
 * (FR-025 / test contract item 10).
 */
const PROOF_FRAGMENT_PATTERN =
  /^#proof=iv1\.([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12})\.([A-Za-z0-9_-]{43})$/;

/**
 * Legacy registration query parameters (FR-025): composite person/nominator
 * `key` values, reversible encrypted person IDs, and reusable awareness
 * identifiers. Their presence marks the invocation as a prohibited legacy
 * shape — the parameters are scrubbed and lookup is never invoked.
 */
const LEGACY_QUERY_PARAMETERS: ReadonlySet<string> = new Set([
  "key",
  "id",
  "personid",
  "encpersonid",
  "membernumber",
  "nominatedbyid",
  "awarenessid",
  "awarenesskey",
  "invitationid",
  "invitekey",
]);

let capturedOnce: InvitationProofCapture | null = null;

/**
 * Identity of the current staged capture's claim capability. Compared by
 * reference: a symbol cannot be forged or re-derived, so only the capture
 * object handed to the performing mount grants access to the staged proof.
 */
let stagedTicket: InvitationProofClaimTicket | null = null;

/**
 * Abandoned-render bound: a COMMITTED mount claims the staged capture within
 * the same tick (commit-time parking, then the lookup effect calls
 * `clearCapturedInvitationProof`), so this window only matters for renders
 * that never commit (error boundary, aborted concurrent render). If the
 * staged proof is still unclaimed when the timer fires, it self-scrubs so a
 * later proof-less mount can never claim a leaked capture. Timer expiry
 * memoizes a proof-less capture (see expireStagedCapture) — it never
 * re-arms a fresh URL parse.
 */
const UNCLAIMED_CAPTURE_SELF_SCRUB_MS = 30_000;

let selfScrubTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Absolute wall-clock deadline for the staged capture. Browser timers only
 * guarantee a minimum delay — a backgrounded/throttled tab can deliver the
 * self-scrub callback arbitrarily late — so every memoized access also
 * enforces this deadline synchronously before returning the staged proof.
 */
let stagedCaptureExpiresAt: number | null = null;

function cancelSelfScrubTimer(): void {
  if (selfScrubTimer !== null) {
    clearTimeout(selfScrubTimer);
    selfScrubTimer = null;
  }
}

export function parseInvitationProofFragment(fragment: string): string | null {
  const match = PROOF_FRAGMENT_PATTERN.exec(fragment);
  if (!match) {
    return null;
  }

  const selector = (match[1] as string).toLowerCase();
  const secret = match[2] as string;
  if (!isCanonicalProofSecret(secret)) {
    return null;
  }

  return `iv1.${selector}.${secret}`;
}

/**
 * Backend-parity canonicality: the secret must decode to exactly 32 bytes
 * and re-encode to the identical string (unpadded base64url), so trailing
 * padding-bit variants of the same bytes never parse as a proof.
 */
function isCanonicalProofSecret(secret: string): boolean {
  let decoded: string;
  try {
    decoded = atob(`${secret.replace(/-/g, "+").replace(/_/g, "/")}=`);
  } catch {
    return false;
  }

  if (decoded.length !== 32) {
    return false;
  }

  const reencoded = btoa(decoded).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  return reencoded === secret;
}

function findLegacyQueryParameterNames(search: string): string[] {
  const parameters = new URLSearchParams(search);
  const legacyNames: string[] = [];
  for (const name of parameters.keys()) {
    if (LEGACY_QUERY_PARAMETERS.has(name.toLowerCase()) && !legacyNames.includes(name)) {
      legacyNames.push(name);
    }
  }
  return legacyNames;
}

/**
 * Commit-time claim gate: a component captures the proof during render (into
 * a fiber-local ref) but must not park a render-phase copy into the
 * ownership holder after the abandoned-render deadline — a retained
 * concurrent render could otherwise commit an expired secret. Returns true
 * only when the caller presents the capture's own claim ticket AND the
 * staged capture is still current AND matches the render-phase copy;
 * enforces the deadline exactly like `captureInvitationProofFromLocation`.
 * The ticket requirement means a mount that did not perform the capture can
 * never park a proof staged by an abandoned render. The staged copy itself
 * is left in place (the caller's post-park cleanup calls
 * `clearCapturedInvitationProof`).
 */
export function claimCapturedInvitationProof(
  ticket: InvitationProofClaimTicket | null,
  expected: string,
): boolean {
  if (stagedCaptureExpiresAt !== null && Date.now() >= stagedCaptureExpiresAt) {
    // Expiry here must memoize proof-less like every other expiry path — a
    // full reset would let a newly injected fragment be re-parsed one-shot.
    expireStagedCapture();
    return false;
  }
  return (
    ticket !== null &&
    ticket === stagedTicket &&
    capturedOnce !== null &&
    capturedOnce.proof === expected
  );
}

/**
 * Strips any fragment and legacy registration query parameters (FR-025)
 * from the address bar/history before anything else can observe or transmit
 * them; benign query parameters survive. Returns the raw fragment for the
 * initial capture to parse.
 */
function scrubProofBearingLocation(): { fragment: string; hasLegacyParameters: boolean } {
  const fragment = window.location.hash;
  const legacyParameterNames = findLegacyQueryParameterNames(window.location.search);

  if (fragment || legacyParameterNames.length > 0) {
    const parameters = new URLSearchParams(window.location.search);
    for (const name of legacyParameterNames) {
      parameters.delete(name);
    }
    const search = parameters.toString();
    window.history.replaceState(
      window.history.state as unknown,
      "",
      window.location.pathname + (search ? `?${search}` : ""),
    );
  }

  return { fragment, hasLegacyParameters: legacyParameterNames.length > 0 };
}

/**
 * One-shot per page load: the first call strips the fragment, memoizes the
 * capture, and mints a claim ticket for the performing mount, so StrictMode
 * re-renders/remounts of the SAME mount (which hold the ticket in a
 * fiber-local ref and present it here) do not lose the proof after the URL
 * has already been scrubbed. A caller WITHOUT the ticket receives a
 * proof-less capture — a later unrelated mount can never consume a stale
 * capture staged by an abandoned render (the 30s deadline + timer remain
 * the backstop for the staged copy itself). Malformed fragments are removed
 * too, without surfacing any client-side parsing detail. Legacy
 * registration query parameters (FR-025) are scrubbed as well and force a
 * proof-less capture so lookup is never invoked for a legacy-shaped
 * invocation. While a capture is staged, the URL is STILL inspected on
 * every call: any newly present fragment or legacy parameter is scrubbed
 * immediately but never parsed, staged, or returned (one-shot). The same
 * holds once the staged capture EXPIRES — whether via the access-time
 * deadline or the self-scrub timer: BOTH paths scrub the expired copy and
 * memoize a proof-less capture — the URL is never re-parsed, so a fragment
 * injected after the first capture can never be adopted.
 */
export function captureInvitationProofFromLocation(
  ticket: InvitationProofClaimTicket | null = null,
): InvitationProofCapture {
  if (capturedOnce) {
    // Enforce the abandoned-render deadline on access, not just on timer
    // delivery: expired staged proofs are scrubbed before anything can
    // observe them. One-shot still applies — the page load's single
    // capture already happened, so anything newly present in the URL is
    // scrubbed and DISCARDED, never parsed or returned.
    if (
      capturedOnce.proof !== null &&
      stagedCaptureExpiresAt !== null &&
      Date.now() >= stagedCaptureExpiresAt
    ) {
      return expireStagedCapture();
    }
    // Even on the memoized short-circuit, never leave a proof-bearing
    // URL behind: scrub and DISCARD anything newly present.
    scrubProofBearingLocation();
    // Capability gate: only the performing mount's ticket re-reads the
    // staged proof; everyone else gets a proof-less capture.
    if (capturedOnce.proof === null || (ticket !== null && ticket === stagedTicket)) {
      return capturedOnce;
    }
    return { proof: null, ticket: null };
  }

  const { fragment, hasLegacyParameters } = scrubProofBearingLocation();

  capturedOnce = {
    proof: hasLegacyParameters ? null : parseInvitationProofFragment(fragment),
    ticket: null,
  };
  if (capturedOnce.proof !== null) {
    // Mint the claim capability for the performing mount, then arm the
    // one-shot self-scrub for captures leaked by an abandoned render; any
    // claim/clear path cancels it. Cancel-then-arm keeps at most one timer.
    // The absolute deadline is authoritative (checked on access); the timer
    // is best-effort eager cleanup that EXPIRES the staged capture exactly
    // like the access-path deadline (scrub + memoized proof-less capture,
    // never a reset that would allow a URL re-parse), GENERATION-SCOPED via
    // its own minted ticket so a stale timer can never clobber newer state.
    const mintedTicket = Symbol("invitation-proof-claim");
    stagedTicket = mintedTicket;
    capturedOnce.ticket = mintedTicket;
    stagedCaptureExpiresAt = Date.now() + UNCLAIMED_CAPTURE_SELF_SCRUB_MS;
    cancelSelfScrubTimer();
    selfScrubTimer = setTimeout(() => {
      selfScrubTimer = null;
      if (mintedTicket !== stagedTicket) {
        // Stale timer: a newer capture is staged — leave it intact.
        return;
      }
      expireStagedCapture();
    }, UNCLAIMED_CAPTURE_SELF_SCRUB_MS);
  }
  return capturedOnce;
}

/**
 * Shared expiry for the CURRENTLY staged capture (access-time deadline and
 * the self-scrub timer): scrubs the staged proof, strips any proof-bearing
 * URL unparsed, and memoizes a proof-less capture so the one-shot guarantee
 * survives expiry — later calls never re-parse the URL.
 */
function expireStagedCapture(): InvitationProofCapture {
  clearCapturedInvitationProof();
  // Memoize BEFORE scrubbing: if history.replaceState throws (e.g. a
  // SecurityError), expiry still fails closed to proof-less instead of
  // leaving the one-shot re-armed for a fresh parse.
  capturedOnce = { proof: null, ticket: null };
  scrubProofBearingLocation();
  return capturedOnce;
}

/**
 * Scrubs the in-memory proof (terminal states, unmount) and resets the
 * one-shot capture so a later navigation performs a fresh capture. The held
 * capture object itself is scrubbed so no retained reference keeps the proof.
 *
 * Called WITHOUT an argument this is the UNCONDITIONAL security scrub
 * (internal deadline-expiry paths and explicit full resets). Called WITH a
 * ticket it is generation-scoped: it clears only when the presented ticket
 * identifies the CURRENTLY staged capture — a null ticket matches only a
 * proof-less staging — so a replaced flow's late cleanup (unmount effect
 * firing after a newer flow staged its own capture in the same commit) can
 * never erase the newer flow's proof.
 */
export function clearCapturedInvitationProof(
  ...scope: [] | [ticket: InvitationProofClaimTicket | null]
): void {
  if (scope.length === 1 && scope[0] !== stagedTicket) {
    // Stale ticket: a newer capture is staged — leave it intact.
    return;
  }
  cancelSelfScrubTimer();
  stagedCaptureExpiresAt = null;
  stagedTicket = null;
  if (capturedOnce) {
    capturedOnce.proof = null;
    capturedOnce.ticket = null;
    capturedOnce = null;
  }
}
